import { FC, memo, MouseEventHandler, PropsWithChildren, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, MenuItem, Stack, Select, SelectChangeEvent, useTheme, useMediaQuery, Typography, Alert } from "@mui/material";
import IconReplay from "@mui/icons-material/Replay";
import { ReactComponent as IconHamburger } from "assets/icons/hamburger.svg";
import { ReactComponent as IconClose } from "assets/icons/close.svg";
import { APP_LOGO, APP_LOGO_SUBTITLE } from "consts";
import { STANDALONE_ARTICLE, STANDALONE_ARTICLE_ID } from "consts/url.consts";
import getStyles from "./styles";

type Props = PropsWithChildren<{
  showMenu: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  changeLanguage: (language: string) => void;
  onReset?: () => void;
}>;

const Header: FC<Props> = ({ children, showMenu, openMenu, closeMenu, changeLanguage, onReset }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const md = useMediaQuery(theme.breakpoints.down("md"));

  const handleReset: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.preventDefault();
    onReset?.();
  }, [onReset]);

  const handleOpenMenu = useCallback(() => {
    openMenu();
  }, [openMenu]);

  const handleCloseMenu = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  const handleLanguageChange = useCallback((e: SelectChangeEvent) => {
    changeLanguage(e.target.value);
  }, [changeLanguage]);

  return (
    <>
      <Stack component="header" sx={styles.container} className="RlxHeader">
        <Box>
          <IconButton sx={styles.hamburger} onClick={handleOpenMenu}>
            <IconHamburger />
          </IconButton>
        </Box>

        <Box component="img" src={APP_LOGO} alt="Company Logo" sx={styles.logo} />

        {md ? (
          <IconButton color="primary" sx={styles.reset} onClick={handleReset}>
            <IconReplay />
          </IconButton>
        ) : (
          <Button variant="text" size="medium" startIcon={<IconReplay />} onClick={handleReset} sx={styles.reset}>
            {t("Reset")}
          </Button>
        )}

        <Select variant={md ? "outlined" : "filled"} size="small" value={i18n.resolvedLanguage} onChange={handleLanguageChange} sx={styles.languages}>
          <MenuItem value="en">EN</MenuItem>
          <MenuItem value="de">DE</MenuItem>
          <MenuItem value="fr">FR</MenuItem>
          {/* @todo: Enable this once sales confirms */}
          {/* <MenuItem value="pl">PL</MenuItem> */}
        </Select>
      </Stack>

      {md && (
        <Box
          component="aside"
          sx={{
            ...styles.sidebar,
            marginLeft: showMenu ? 0 : "-300px",
            boxShadow: showMenu ? "5px 0 15px -5px rgba(0, 0, 0, 0.5)" : "none",
          }}
        >
          <IconButton sx={styles.close} onClick={handleCloseMenu}>
            <IconClose />
          </IconButton>

          <Stack component="figure" sx={styles.figure}>
            <Box component="img" src={`${APP_LOGO}`} alt="Company Logo" />
            <Box component="figcaption">{t(APP_LOGO_SUBTITLE || "")}</Box>
          </Stack>

          {STANDALONE_ARTICLE && (
            <Alert variant="outlined" severity="info" sx={styles.alert}>
              Running RSC on: <strong>{STANDALONE_ARTICLE}</strong>
              {STANDALONE_ARTICLE_ID}
            </Alert>
          )}

          <Typography variant="h5" sx={styles.subtitle}>
            ReluxStreetCalc
          </Typography>

          {children}
        </Box>
      )}
    </>
  );
};

Header.displayName = "Header";

export default memo(Header);
